<script module>
    import ControlPanel from "../control/Controller.svelte";
		import MasterPanel from "../control/Master.svelte"
    import NotesPanel from "../panels/NotesPanel.svelte";
    import ImagePanel from "../panels/ImagePanel.svelte";
    import MasterTradePanel from "../panels/MasterTradePanel.svelte";
    import TradePanel from "../panels/TradePanel.svelte";
    import Obtain from "./Obtain.svelte";
    import Encounter from "./Encounter.svelte";
    import Journal from "./Journal.svelte";
    import Shop from "./Shop.svelte";
    import TradeDesk from "./TradeDesk.svelte";
    import ExhibCatalog from "./ExhibCatalog.svelte";
    import Story from "./Story.svelte";
    import PlotPanel from "../panels/PlotPanel.svelte";

		export const playLayouts = [
				{page: 'overview', Page: Story, panel: 'plot', Master: PlotPanel, label: "Abenteuer-Übersicht"},
				{page: 'journal', Page: Journal, Side: ControlPanel, panel: 'notes', Master: NotesPanel, label: "Aufzeichnungen"},
				{page: 'present', Page: ExhibCatalog, Side: ControlPanel, panel: 'exhibits', Master: ImagePanel, label: "Bilder"},
				{page: 'trade', Page: Obtain, PlayerPage: Shop, MasterPage: TradeDesk, Side: TradePanel, Master: MasterTradePanel, label: "Handeln"},
				{page: 'control', Page: Encounter, Side: ControlPanel, panel: 'control', Master: ControlPanel, label: "Persona-Steuerung"},
				{page: 'encounter', Page: Encounter, Side: ControlPanel, panel: 'master', Master: MasterPanel, label: "Begegnung"},
			].map(layout => Object.assign(layout, {module: 'play'}))
</script>

<script>
    import { user, game, view } from "../client.svelte.js"
		import TimerBox from "./Timer.svelte";
		import Timeline from "./Timeline.svelte";
    import StructPanel from "../panels/StructPanel.svelte";
		import TimelineHandle from "./TimelineHandle.svelte";
		import OccurredToasts from "./OccurredToasts.svelte";
		import Breadcrumbs from "../elements/Breadcrumbs.svelte";
		import NavButton from "../elements/NavButton.svelte";
		import HelpButton from "../elements/HelpButton.svelte";
		import Help from "../elements/Help.svelte";
		import {desk} from "../panels/MasterTradePanel.svelte";

		/** trade page of the master: loot management (Obtain) or wish list of the hero selected in MasterTradePanel **/
		let Page = $derived((user.isMaster) ? (desk.heroID && view.layout.MasterPage) || view.layout.Page
		                                    : view.layout.PlayerPage || view.layout.Page)
		let sectionDialog

		let visibleTabs = $derived(playLayouts.filter(layout =>
	    (layout.page !== 'encounter' || game.inEncounter) &&
	    (layout.page !== 'overview' || user.isMaster) &&
	    (layout.page !== 'master' || user.isMaster) &&
	    (layout.page !== 'control' || user.isMaster) ) )
		let Control = $derived((user.isMaster) ? view.layout.Master : view.layout.Side)

</script>


<!-------------------------------- HTML -------------------------------------->

<main id='play' class:ismaster={user.isMaster}>

	<div class="mnav">
		<nav class="tabs-box">
			{#each visibleTabs as layout}
				<button class=tab class:selected={view.layout.page === layout.page} onclick={() => view.switch_layout(`play-${layout.page}`)}>
					<img src="/static/buttons/{layout.page}.png" alt={layout} title={layout.label}>
				</button>
			{/each}
		</nav>
	</div>

	{#if user.isMaster}
		<header>
			<Breadcrumbs area="auto" crumbs={[{label: view.adventure?.name ?? "Abenteuer"}]}
			           current={view.section?.name ?? "Abenteuerübersicht"}>
				<span class="section-box" data-help="sectionselect">
					<NavButton icon="list-ol" label="Abschnitt" title="Abschnitt auswählen" disabled={game.inEncounter}
					           onclick={() => sectionDialog.showModal()} />
					{#if view.section && game.inEncounter}
						<NavButton icon="flag" label="leave" title="Begegnung verlassen"
						           onclick={game.leave_encounter} />
					{:else if view.section}
						{@const isCurrentSection = (game.section === view.section.ID)}
						<NavButton icon="flag" active={isCurrentSection} label={isCurrentSection ? "aktuell" : "festlegen"}
						           title={isCurrentSection ? "ist aktueller Abschnitt" : "als aktuellen Abschnitt festlegen"}
						           onclick={game.switch.bind(game, view.section.ID)} />
					{/if}
				</span>
				{#snippet end()}
					{#if game.inEncounter}
						<TimerBox />
					{/if}
				{/snippet}
			</Breadcrumbs>

			<dialog bind:this={sectionDialog} id="section-selection" onclick={() => sectionDialog.close()}>
				<StructPanel/>
			</dialog>
		</header>
	{:else}
		<!-- player: no navigation bar, only help button and timer in the top right corner -->
		<div class="corner">
			<HelpButton/>
			{#if game.inEncounter}
				<TimerBox />
			{/if}
		</div>
	{/if}

	<aside class="control">
		<Control/>
	</aside>

	<Page />

	{#if view.timelineShown}
		<Timeline />
	{:else}
		<div class="timeline-bar">
			<TimelineHandle/>
		</div>
		<OccurredToasts/>
	{/if}

	<Help/>

</main>


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	#play
		--timeline-width: 0px
		--header-height: 40px
		--panel-width: 380px
		display: grid
		grid-gap: 12px
		grid-template-columns: var(--panel-width) minmax(0, 1fr) var(--timeline-width) var(--help-width, 0)
		grid-template-rows: var(--header-height) auto 30px
		grid-template-areas: "mnav main time ."   "control main timeline help"   "control main foot help"

		&.ismaster
			--header-height: 60px
			--panel-width: 420px
			grid-template-areas: "mnav head head head"   "control main timeline help"   "control main foot help"

	/* — tabs to the play pages next to the main menu button — */
	.mnav
		grid-area: mnav
		padding-left: var(--menu-width)

	/* — Breadcrumbs bar of the master (header spans to the right end of the screen) — */
	header
		display: grid

	.section-box
		display: flex
		gap: 4px

	/* — help button and timer of the player in the top right corner — */
	.corner
		position: absolute
		top: 0
		right: 0
		z-index: 9
		height: 60px
		display: flex
		align-items: center
		gap: 8px
		padding-left: 8px
		background-color: #111111

	.control
		grid-area: control

	#section-selection
		margin: unset
		background-color: black
		color: colors.$white
		top: 62px
		left: 500px

	/* — retracted timeline: narrow bar with handle on the right side of the screen — */
	.timeline-bar
		position: absolute
		right: 0
		top: 80px
		bottom: 0
		z-index: 8
		width: 8px
		background: #c6b492


	.tabs-box
		grid-area: tabs
		display: flex
		margin-bottom: 10px

	.tab
		cursor: pointer
		padding: 4px 8px
		border: 1px solid transparent
		margin-bottom: -1px
		background-color: colors.$grey-20
		border-bottom-color: colors.$grey-50

		img
			width: 36px

	.tab.selected
		border-top-right-radius: 8px
		border-top-left-radius: 8px
		border-top-width: 8px
		border-color: colors.$primary-100 colors.$grey-50 colors.$grey-20


	pinch-zoom
		width: 100%
		height: 100%

	h1
		margin: 0


	aside#panel
		position: fixed
		left: 0
		top: 58px  /* for player */
		width: 380px
		height: calc(100vh - 58px)
		overflow: auto
		padding: 8px
		scrollbar-width: none  /* for Firefox */
		background: black url("/static/images/background-light.png")

		&.master
			top: 102px
			width: 420px
			height: calc(100vh - 102px)


	/*** Layout navigation via tabs next to dragon menu ***/

	.tab
		cursor: pointer
		padding: 4px 6px
		border: 1px solid transparent
		margin-bottom: -1px
		background-color: colors.$grey-20
		border-bottom-color: colors.$grey-50

	.tab.selected
		border-top-right-radius: 8px
		border-top-left-radius: 8px
		border-top-width: 8px
		border-color: colors.$primary-100 colors.$grey-50 colors.$grey-20

	.tab img
		width: 36px



</style>